import { useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { cargoService, departamentoService } from '@/services/api';
import { Cargo, Departamento, ResultadoImportacaoCargos } from '@/types';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Plus, Briefcase, Edit, Trash2, Loader2, RotateCcw, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Navigate } from 'react-router-dom';
import { UnsavedChangesDialog } from '@/components/ui/unsaved-changes-dialog';
import { GuiaModuloFlutuante } from '@/components/shared/GuiaModuloFlutuante';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CrmImportCsvDialog } from '@/components/crm/CrmImportCsvDialog';
import { baixarModeloImportacaoCargos, COLUNAS_IMPORTACAO_CARGOS } from '@/lib/cargoImportModelo';

type Visao = 'ativos' | 'lixeira';

const MSG_NOME_DUPLICADO = 'Já existe um cargo com esse nome nesta empresa';
const LIMITE_QUANTIDADE = 99999;
/** Radix Select não aceita value="" em item: "Nenhum" usa este valor e vira `null` ao salvar. */
const SEM_DEPARTAMENTO = 'nenhum';

/** Quantidade digitada → número, `null` (vazio) ou `undefined` (inválida). */
const lerQuantidade = (texto: string): number | null | undefined => {
  const limpo = texto.trim();
  if (!limpo) return null;
  if (!/^\d+$/.test(limpo)) return undefined; // Number() aceitaria "0x10" e "1e2"
  const numero = Number(limpo);
  return Number.isInteger(numero) && numero >= 0 && numero <= LIMITE_QUANTIDADE ? numero : undefined;
};

const formatarData = (iso: string | null): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString('pt-BR');
};

export default function Cargos() {
  const { user, empresaAtiva, empresaFiltroId } = useAuth();
  const isSuperAdmin = user?.tipo === 'superadmin';
  // Empresa vem do seletor global do header: pro superadmin é o `empresaFiltroId` (pode ser
  // "todas as empresas"); pra admin/comum é a empresa vinculada.
  const selectedEmpresa = (isSuperAdmin ? empresaFiltroId : empresaAtiva?.id) ?? (isSuperAdmin ? 'all' : '');

  const [visao, setVisao] = useState<Visao>('ativos');
  const [cargos, setCargos] = useState<Cargo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [busca, setBusca] = useState('');

  // Diálogo de criar/editar
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCargo, setEditingCargo] = useState<Cargo | null>(null);
  const [nome, setNome] = useState('');
  const [nomeInitial, setNomeInitial] = useState('');
  const [quantidade, setQuantidade] = useState('');
  const [departamentoId, setDepartamentoId] = useState(SEM_DEPARTAMENTO);
  const [inicialOpcionais, setInicialOpcionais] = useState({ quantidade: '', departamentoId: SEM_DEPARTAMENTO });
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [erroNome, setErroNome] = useState('');
  const [erroQuantidade, setErroQuantidade] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [showUnsaved, setShowUnsaved] = useState(false);

  // Confirmações
  const [deleteCargo, setDeleteCargo] = useState<Cargo | null>(null);
  const [cargoEmUso, setCargoEmUso] = useState<{ nome: string; totalPessoas: number } | null>(null);
  // Criar com nome de um cargo excluído → oferece restaurar o existente
  const [restaurarOferta, setRestaurarOferta] = useState<{ cargoId: string; nome: string } | null>(null);

  const hasChanges =
    nome !== nomeInitial ||
    quantidade !== inicialOpcionais.quantidade ||
    departamentoId !== inicialOpcionais.departamentoId;
  const semEmpresa = isSuperAdmin && selectedEmpresa === 'all';
  const empresaDosCargos = isSuperAdmin ? selectedEmpresa : empresaAtiva?.id || user?.empresa_id || '';

  // Departamentos da empresa: select do formulário (o cargo pode ficar sob um departamento no organograma).
  useEffect(() => {
    if (semEmpresa || !empresaDosCargos) {
      setDepartamentos([]);
      return;
    }
    let cancelado = false;
    departamentoService.getByEmpresa(empresaDosCargos).then((res) => {
      if (!cancelado && res.success) {
        setDepartamentos([...res.data].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')));
      }
    });
    return () => {
      cancelado = true;
    };
  }, [semEmpresa, empresaDosCargos]);

  const loadData = useCallback(async () => {
    // Cargos é por empresa — "Todas as empresas" não se aplica.
    if (semEmpresa) {
      setCargos([]);
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      const result = await cargoService.getAll({
        lixeira: visao === 'lixeira',
        empresa_id: isSuperAdmin ? selectedEmpresa : undefined,
      });
      if (result.success) {
        setCargos(result.data);
      } else {
        toast.error(result.error || 'Erro ao carregar cargos');
      }
    } catch (error) {
      console.error('Error loading cargos:', error);
      toast.error('Erro ao carregar cargos');
    } finally {
      setIsLoading(false);
    }
  }, [semEmpresa, visao, isSuperAdmin, selectedEmpresa]);

  useEffect(() => {
    if (user?.tipo === 'admin' || user?.tipo === 'superadmin') {
      loadData();
    } else {
      setIsLoading(false);
    }
  }, [user, loadData]);

  const cargosFiltrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return cargos;
    return cargos.filter((c) => c.nome.toLowerCase().includes(termo));
  }, [cargos, busca]);

  const fecharDialogo = () => {
    setIsDialogOpen(false);
    setNome('');
    setNomeInitial('');
    setQuantidade('');
    setDepartamentoId(SEM_DEPARTAMENTO);
    setInicialOpcionais({ quantidade: '', departamentoId: SEM_DEPARTAMENTO });
    setErroNome('');
    setErroQuantidade('');
    setEditingCargo(null);
  };

  const handleCloseDialog = () => {
    if (hasChanges) {
      setShowUnsaved(true);
    } else {
      fecharDialogo();
    }
  };

  const openNew = () => {
    setNome('');
    setNomeInitial('');
    setQuantidade('');
    setDepartamentoId(SEM_DEPARTAMENTO);
    setInicialOpcionais({ quantidade: '', departamentoId: SEM_DEPARTAMENTO });
    setErroNome('');
    setErroQuantidade('');
    setEditingCargo(null);
    setIsDialogOpen(true);
  };

  const openEdit = (cargo: Cargo) => {
    const opcionais = {
      quantidade: cargo.quantidade_quadro === null ? '' : String(cargo.quantidade_quadro),
      departamentoId: cargo.departamento_id ?? SEM_DEPARTAMENTO,
    };
    setEditingCargo(cargo);
    setNome(cargo.nome);
    setNomeInitial(cargo.nome);
    setQuantidade(opcionais.quantidade);
    setDepartamentoId(opcionais.departamentoId);
    setInicialOpcionais(opcionais);
    setErroNome('');
    setErroQuantidade('');
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    const nomeLimpo = nome.trim();
    if (!nomeLimpo) {
      setErroNome('Informe o nome do cargo');
      return;
    }
    const quantidadeQuadro = lerQuantidade(quantidade);
    if (quantidadeQuadro === undefined) {
      setErroQuantidade(`Use um número inteiro de 0 a ${LIMITE_QUANTIDADE}`);
      return;
    }
    const campos = {
      nome: nomeLimpo,
      quantidade_quadro: quantidadeQuadro,
      departamento_id: departamentoId === SEM_DEPARTAMENTO ? null : departamentoId,
    };
    setSalvando(true);
    setErroNome('');
    setErroQuantidade('');
    try {
      const result = editingCargo
        ? await cargoService.update(editingCargo.id, campos)
        : await cargoService.create({
            ...campos,
            empresa_id: isSuperAdmin ? selectedEmpresa : user?.empresa_id,
          });

      if (result.success) {
        toast.success(editingCargo ? 'Cargo atualizado' : 'Cargo criado');
        fecharDialogo();
        loadData();
      } else if (result.code === 'NOME_DUPLICADO') {
        setErroNome(MSG_NOME_DUPLICADO);
      } else if (result.code === 'NOME_NA_LIXEIRA') {
        if (!editingCargo && result.cargo_id) {
          setRestaurarOferta({ cargoId: result.cargo_id, nome: nomeLimpo });
        } else {
          // Renomear para o nome de um cargo excluído: restaurar não resolve, só avisa
          setErroNome('Existe um cargo excluído com esse nome (veja a Lixeira)');
        }
      } else {
        toast.error(result.error || 'Erro ao salvar cargo');
      }
    } catch (error) {
      toast.error('Erro ao salvar cargo');
    } finally {
      setSalvando(false);
    }
  };

  const restaurarCargo = async (cargoId: string) => {
    try {
      const result = await cargoService.restaurar(cargoId);
      if (result.success) {
        toast.success('Cargo restaurado');
        return true;
      }
      if (result.code === 'NOME_DUPLICADO') {
        toast.error('Já existe um cargo ativo com esse nome. Renomeie um dos dois e tente de novo.');
      } else {
        toast.error(result.error || 'Erro ao restaurar cargo');
      }
    } catch (error) {
      toast.error('Erro ao restaurar cargo');
    }
    return false;
  };

  const handleRestaurarDaOferta = async () => {
    if (!restaurarOferta) return;
    const ok = await restaurarCargo(restaurarOferta.cargoId);
    setRestaurarOferta(null);
    if (ok) {
      fecharDialogo();
      setVisao('ativos');
      loadData();
    }
  };

  const handleRestaurarDaLixeira = async (cargo: Cargo) => {
    if (await restaurarCargo(cargo.id)) loadData();
  };

  const handleDelete = async () => {
    if (!deleteCargo) return;
    const alvo = deleteCargo;
    setDeleteCargo(null);
    try {
      const result = await cargoService.remove(alvo.id);
      if (result.success) {
        toast.success('Cargo excluído (fica na Lixeira e pode ser restaurado)');
        loadData();
      } else if (result.code === 'CARGO_EM_USO') {
        setCargoEmUso({ nome: alvo.nome, totalPessoas: result.total_pessoas ?? alvo.total_pessoas });
      } else {
        toast.error(result.error || 'Erro ao excluir cargo');
      }
    } catch (error) {
      toast.error('Erro ao excluir cargo');
    }
  };

  // Só admin e superadmin acessam esta página
  if (user?.tipo !== 'admin' && user?.tipo !== 'superadmin') {
    return <Navigate to="/dashboard" replace />;
  }

  if (semEmpresa) {
    return (
      <AppLayout>
        <div className="flex min-h-[320px] flex-col items-center justify-center rounded-lg border border-dashed bg-muted/20 p-8 text-center">
          <h2 className="text-lg font-semibold">Selecione uma empresa</h2>
          <p className="mt-1 max-w-md text-sm text-muted-foreground">
            Cargos é por empresa — escolha uma empresa específica no filtro do topo (está em "Todas as empresas").
          </p>
        </div>
        <GuiaModuloFlutuante imagemSrc="/guias-modulos/cargos.png" titulo="Cargos" />
      </AppLayout>
    );
  }

  const naLixeira = visao === 'lixeira';

  return (
    <AppLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="rounded-lg border bg-card p-4 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Cargos</h1>
            <p className="text-muted-foreground">
              {isSuperAdmin ? 'Gerencie os cargos das empresas' : 'Gerencie os cargos da sua empresa'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div data-tour="importar-cargos" className="inline-flex">
            <CrmImportCsvDialog<ResultadoImportacaoCargos>
              titulo="Importar cargos"
              templateNomeArquivo="modelo_importacao_cargos.xlsx"
              templateColunas={COLUNAS_IMPORTACAO_CARGOS}
              templateExemplo={[]}
              accept=".xlsx,.csv"
              rotuloModelo="Baixar modelo Excel"
              onBaixarModelo={baixarModeloImportacaoCargos}
              ajuda="Colunas: nome (obrigatória), quantidade e departamento (opcionais). Nomes ficam em maiúsculo; departamento que não existir é criado. Aceita .xlsx e .csv, até 500 linhas."
              onImport={(file) => cargoService.importar(file, isSuperAdmin ? selectedEmpresa : null)}
              detalheResultado={(r) =>
                [
                  `${r.criados} criado(s)`,
                  `${r.atualizados} atualizado(s)`,
                  r.sem_alteracao ? `${r.sem_alteracao} sem alteração` : '',
                  r.departamentos_criados?.length
                    ? `departamento(s) criado(s): ${r.departamentos_criados.join(', ')}`
                    : '',
                ]
                  .filter(Boolean)
                  .join(' · ')
              }
              onSuccess={() => {
                setVisao('ativos');
                loadData();
              }}
            />
            </div>
            <Button onClick={openNew} data-tour="novo-cargo">
              <Plus className="h-4 w-4 mr-2" />
              Novo Cargo
            </Button>
          </div>
        </div>

        <Card>
          <CardContent className="p-0">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b p-4">
              <Tabs value={visao} onValueChange={(v) => { setVisao(v as Visao); setBusca(''); }}>
                <TabsList>
                  <TabsTrigger value="ativos">Ativos</TabsTrigger>
                  <TabsTrigger value="lixeira">Lixeira</TabsTrigger>
                </TabsList>
              </Tabs>
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder="Buscar cargo"
                  className="pl-8"
                />
              </div>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cargo</TableHead>
                    {naLixeira ? (
                      <TableHead>Excluído em</TableHead>
                    ) : (
                      <>
                        <TableHead className="hidden md:table-cell">Departamento</TableHead>
                        <TableHead>Pessoas</TableHead>
                        <TableHead title="Quantidade de pessoas no quadro (informada)">Quadro</TableHead>
                      </>
                    )}
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cargosFiltrados.map((cargo) => (
                    <TableRow key={cargo.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Briefcase className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{cargo.nome}</span>
                        </div>
                      </TableCell>
                      {naLixeira ? (
                        <TableCell>
                          <span className="text-sm text-muted-foreground">{formatarData(cargo.deletado_em)}</span>
                        </TableCell>
                      ) : (
                        <>
                          <TableCell className="hidden md:table-cell">
                            <span className="text-sm text-muted-foreground">{cargo.departamento_nome || '—'}</span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-muted-foreground">{cargo.total_pessoas}</span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-muted-foreground">{cargo.quantidade_quadro ?? '—'}</span>
                          </TableCell>
                        </>
                      )}
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {naLixeira ? (
                            <Button variant="outline" size="sm" onClick={() => handleRestaurarDaLixeira(cargo)}>
                              <RotateCcw className="h-4 w-4 mr-2" />
                              Restaurar
                            </Button>
                          ) : (
                            <>
                              <Button variant="ghost" size="icon" onClick={() => openEdit(cargo)} title="Editar">
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => setDeleteCargo(cargo)} title="Excluir">
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}

                  {cargosFiltrados.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={naLixeira ? 3 : 5} className="text-center py-8 text-muted-foreground">
                        {busca.trim()
                          ? 'Nenhum cargo encontrado'
                          : naLixeira
                            ? 'A lixeira está vazia'
                            : 'Nenhum cargo cadastrado'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Criar / editar */}
      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          if (!open) handleCloseDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCargo ? 'Editar Cargo' : 'Novo Cargo'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cargo_nome">Nome do Cargo</Label>
              <Input
                id="cargo_nome"
                value={nome}
                // Padrão do cadastro: TUDO MAIÚSCULO. O backend grava assim; aqui é só visual (CSS), para o cursor
                // não pular para o fim ao editar no meio do nome.
                onChange={(e) => {
                  setNome(e.target.value);
                  if (erroNome) setErroNome('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && nome.trim() && !salvando) handleSave();
                }}
                placeholder="Ex: GERENTE COMERCIAL"
                aria-invalid={!!erroNome}
                className={erroNome ? 'uppercase border-destructive' : 'uppercase'}
              />
              {erroNome && <p className="text-sm text-destructive">{erroNome}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="cargo_departamento">Departamento (opcional)</Label>
              <Select value={departamentoId} onValueChange={setDepartamentoId}>
                <SelectTrigger id="cargo_departamento">
                  <SelectValue placeholder="Nenhum" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={SEM_DEPARTAMENTO}>Nenhum</SelectItem>
                  {departamentos.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Onde o cargo aparece no organograma gerado quando ninguém cadastrado o ocupa.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cargo_quantidade">Quantidade no quadro (opcional)</Label>
              <Input
                id="cargo_quantidade"
                type="number"
                inputMode="numeric"
                min={0}
                max={LIMITE_QUANTIDADE}
                step={1}
                value={quantidade}
                onChange={(e) => {
                  setQuantidade(e.target.value);
                  if (erroQuantidade) setErroQuantidade('');
                }}
                placeholder="Ex: 10"
                aria-invalid={!!erroQuantidade}
                className={erroQuantidade ? 'border-destructive' : undefined}
              />
              {erroQuantidade ? (
                <p className="text-sm text-destructive">{erroQuantidade}</p>
              ) : (
                <p className="text-xs text-muted-foreground">Número informado de pessoas no cargo. Não se atualiza sozinho.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={!nome.trim() || salvando}>
              {editingCargo ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UnsavedChangesDialog
        open={showUnsaved}
        onOpenChange={setShowUnsaved}
        onConfirm={() => {
          setShowUnsaved(false);
          fecharDialogo();
        }}
        onCancel={() => setShowUnsaved(false)}
      />

      {/* Nome igual ao de um cargo excluído → oferece restaurar */}
      <AlertDialog open={!!restaurarOferta} onOpenChange={(open) => { if (!open) setRestaurarOferta(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cargo excluído com esse nome</AlertDialogTitle>
            <AlertDialogDescription>
              Já existe um cargo excluído com esse nome. Restaurar "{restaurarOferta?.nome}"?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleRestaurarDaOferta}>Restaurar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmar exclusão */}
      <AlertDialog open={!!deleteCargo} onOpenChange={(open) => { if (!open) setDeleteCargo(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Excluir o cargo "{deleteCargo?.nome}"? Ele vai para a Lixeira e pode ser restaurado depois.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cargo em uso: exclusão bloqueada */}
      <AlertDialog open={!!cargoEmUso} onOpenChange={(open) => { if (!open) setCargoEmUso(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Não é possível excluir</AlertDialogTitle>
            <AlertDialogDescription>
              O cargo "{cargoEmUso?.nome}" está em uso por {cargoEmUso?.totalPessoas}{' '}
              {cargoEmUso?.totalPessoas === 1 ? 'pessoa' : 'pessoas'}. Troque o cargo dessas pessoas
              (em Usuários) e tente excluir de novo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setCargoEmUso(null)}>Entendi</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <GuiaModuloFlutuante imagemSrc="/guias-modulos/cargos.png" titulo="Cargos" />
    </AppLayout>
  );
}
